import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, Loader2, MessageSquareReply, AlertTriangle, CalendarDays } from "lucide-react";
import { HoldToTalk } from "../components/HoldToTalk";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* 免登录：催办短信链接 → PM 手机上填原因（可按住说话）+ 承诺日期 */
export default function NudgeReply() {
  const { token } = useParams();
  const [zh, setZh] = useState(() => (localStorage.getItem("ali_lang") || "zh") === "zh");
  const [n, setN] = useState(null);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [promise, setPromise] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [voiceMsg, setVoiceMsg] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/nudge/${token}`).then((r) => setN(r.data)).catch((e) => setErr(e?.detailText || (zh ? "链接无效" : "Invalid link")));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    if (!text.trim()) return setErr(zh ? "请填写原因" : "Enter a reason");
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/public/nudge/${token}/reply`, { text: text.trim(), promiseDate: promise || undefined });
      setDone(data);
    } catch (e) { setErr(e?.detailText || (zh ? "提交失败" : "Failed")); } finally { setBusy(false); }
  };
  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-6 flex flex-col items-center" data-testid="public-nudge-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <p className="text-xs font-black tracking-[0.25em] text-red-500">ALIGCUS</p>
          <button onClick={() => { setZh(!zh); localStorage.setItem("ali_lang", zh ? "en" : "zh"); }} data-testid="public-nudge-lang" className="text-[11px] text-slate-400 border border-slate-700 rounded-full px-2.5 py-1 hover:text-white">{zh ? "English" : "中文"}</button>
        </div>
        {children}
      </div>
    </div>
  );
  if (err && !n) return <Shell><div className="rounded-2xl bg-slate-900 border border-slate-800 p-5 text-sm text-red-300 flex items-center gap-2" data-testid="public-nudge-error"><AlertTriangle className="h-4 w-4" />{err}</div></Shell>;
  if (!n) return <Shell><p className="text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-1" />{zh ? "加载中…" : "Loading…"}</p></Shell>;
  const replied = done || n.reply;
  return (
    <Shell>
      <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5 space-y-4">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500">{zh ? "进度催办 · 请回复" : "Progress nudge · please reply"}</p>
          <h1 className="text-xl font-black mt-1 leading-tight" data-testid="public-nudge-address" data-keep-title>{n.address}</h1>
          <p className="text-sm text-slate-300 mt-2">
            {zh ? "当前阶段" : "Current phase"} <b className="text-white">{n.phase}</b>
            {n.overDays > 0 && <span className="ml-2 inline-flex items-center gap-1 text-red-400 font-semibold"><Clock className="h-3.5 w-3.5" />{zh ? `已超期 ${n.overDays} 天` : `${n.overDays} days overdue`}</span>}
          </p>
          <p className="text-xs text-slate-500 mt-1">{zh ? "催办人" : "From"} {n.fromName}{n.note && <> · {n.note}</>}</p>
          {n.escalatedAt && <p className="text-xs text-red-400 mt-1 font-semibold" data-testid="public-nudge-escalated">{zh ? "⚠ 已超过 48 小时未回应，工程经理已收到抄送" : "⚠ Unanswered for 48h — construction managers were notified"}</p>}
        </div>
        {replied ? (
          <div className="rounded-xl bg-emerald-950/60 border border-emerald-800 p-4" data-testid="public-nudge-done">
            <p className="text-sm font-semibold text-emerald-300 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />{zh ? "已回复，催办人已收到通知" : "Reply sent — the requester has been notified"}</p>
            <p className="text-sm text-emerald-100 mt-2 whitespace-pre-wrap">{done?.reply || n.reply}</p>
            {(done?.promiseDate || n.promiseDate) && <p className="text-xs text-emerald-300 mt-2 flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{zh ? "预计恢复 / 完成" : "Expected by"} {done?.promiseDate || n.promiseDate}<span className="text-emerald-500 ml-1">{zh ? "· 到期未推进会自动再提醒" : "· auto re-nudge if missed"}</span></p>}
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs text-slate-400">{zh ? "延误原因 / 当前进度 *" : "Reason / current progress *"}
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="public-nudge-text"
                placeholder={zh ? "例如：混凝土供应延迟，本周四恢复浇筑…" : "e.g. concrete delivery delayed, pour resumes Thursday…"}
                className="mt-1 w-full rounded-xl bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </label>
            <HoldToTalk url={`${API}/public/nudge/${token}/transcribe`} zh={zh} disabled={busy}
              onText={(t) => { setText((v) => (v ? `${v} ${t}` : t)); setErr(""); setVoiceMsg(zh ? "已转成文字，可继续修改后提交" : "Transcribed — edit if needed, then send"); }}
              onError={(m) => setVoiceMsg(m)} />
            {voiceMsg && <p className="text-[11px] text-slate-400 -mt-1" data-testid="public-nudge-voice-msg">{voiceMsg}</p>}
            <label className="block text-xs text-slate-400">{zh ? "预计恢复 / 完成日期（可选）" : "Expected resume / finish date (optional)"}
              <input type="date" value={promise} min={n.today} onChange={(e) => setPromise(e.target.value)} data-testid="public-nudge-promise"
                className="mt-1 w-full rounded-xl bg-slate-800 border border-slate-700 px-3 h-11 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
              <span className="block mt-1 text-[11px] text-slate-500">{zh ? "到期当天若阶段仍未推进，系统会自动再提醒你和催办人" : "If the phase hasn't advanced by then, both of you get an automatic reminder"}</span>
            </label>
            {err && <p className="text-xs text-red-400" data-testid="public-nudge-err">{err}</p>}
            <button onClick={send} disabled={busy} data-testid="public-nudge-send"
              className="w-full h-12 rounded-full bg-brand-500 hover:bg-brand-500 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareReply className="h-4 w-4" />}{zh ? "提交回复" : "Send reply"}
            </button>
          </div>
        )}
        <p className="text-[11px] text-slate-600">{zh ? "此链接 7 天内有效，仅限本次催办。更新项目进度请登录系统。" : "Link valid for 7 days, for this nudge only. Log in to update the project itself."}</p>
      </div>
    </Shell>
  );
}
